import { useEffect, useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/auth";
import { Mask } from "@/utils/mask";
import { cn } from "@/lib/utils";
import {
  getUnreadDevolutionNotifications,
  markNotificationAsRead,
  NotificationProps,
} from "@/Api/notifications";

const POLLING_INTERVAL_MS = 10000;
const APPROVED_LABEL = "success";

/**
 * Avisa o criador da devolução quando ela é aprovada ou rejeitada.
 *
 * O modal só fecha pelo botão "Ciente" (sem X, sem clique fora, sem Esc), para
 * garantir que o vendedor leu o resultado e a observação do aprovador.
 */
export function DevolutionNoticeModal() {
  const auth = useAuth();
  const isImpersonating = !!auth?.impersonation;

  const [notifications, setNotifications] = useState<NotificationProps[]>([]);
  const [acknowledging, setAcknowledging] = useState(false);

  const fetchNotifications = async () => {
    try {
      const { data } = await getUnreadDevolutionNotifications();
      const unread: NotificationProps[] = data?.response?.data ?? [];

      // Mais antigas primeiro: o aviso exibido não troca quando chega um novo
      setNotifications(
        [...unread].sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        )
      );
    } catch (error) {
      console.error("Erro ao buscar avisos de devolução:", error);
    }
  };

  useEffect(() => {
    // Admin personificando não pode consumir os avisos do usuário-alvo
    if (isImpersonating) return;

    const refetchWhenVisible = () => {
      if (document.visibilityState === "visible") fetchNotifications();
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, POLLING_INTERVAL_MS);
    // Aba em segundo plano tem timer atrasado: busca assim que o usuário voltar
    document.addEventListener("visibilitychange", refetchWhenVisible);
    window.addEventListener("focus", refetchWhenVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", refetchWhenVisible);
      window.removeEventListener("focus", refetchWhenVisible);
    };
  }, [isImpersonating]);

  const current = notifications[0];

  const handleAcknowledge = async () => {
    if (!current) return;

    setAcknowledging(true);
    try {
      await markNotificationAsRead(current.uuid);
      setNotifications((prev) => prev.filter((n) => n.uuid !== current.uuid));
    } catch (error) {
      console.error("Erro ao confirmar ciência do aviso:", error);
      toast.error("Não foi possível confirmar. Tente novamente.");
    } finally {
      setAcknowledging(false);
    }
  };

  if (isImpersonating || !current) return null;

  const approved = current.type_label?.name === APPROVED_LABEL;
  const Icon = approved ? CheckCircle2 : XCircle;
  // Avisos antigos não têm `data`: caem no texto corrido da mensagem
  const details = current.data;
  const decidedAt = format(new Date(current.created_at), "dd/MM/yyyy 'às' HH:mm", {
    locale: ptBR,
  });

  return (
    <AlertDialog open>
      <AlertDialogContent className="max-w-lg gap-0 overflow-hidden p-0">
        <div
          className={cn(
            "flex items-center gap-4 px-6 py-5 text-white",
            approved ? "bg-green-600" : "bg-red-600"
          )}
        >
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white/20">
            <Icon className="h-9 w-9" strokeWidth={2.25} />
          </span>
          <AlertDialogHeader className="space-y-0.5">
            <AlertDialogTitle className="text-2xl font-bold">
              {current.title}
            </AlertDialogTitle>
            <p className="text-sm text-white/90">
              Sua solicitação de devolução foi{" "}
              <strong>{approved ? "aprovada" : "rejeitada"}</strong>.
            </p>
          </AlertDialogHeader>
        </div>

        <AlertDialogDescription asChild className="text-foreground">
          <div className="space-y-4 px-6 py-5">
            {details ? (
              <>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border bg-muted/40 p-4 text-sm">
                  <div className="col-span-2">
                    <dt className="text-muted-foreground">Cliente</dt>
                    <dd className="font-medium">{details.client_name}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Valor</dt>
                    <dd className="font-medium">
                      {Mask.formatCurrency(Number(details.value))}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Data e hora</dt>
                    <dd className="font-medium">{decidedAt}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-muted-foreground">
                      {approved ? "Aprovada por" : "Rejeitada por"}
                    </dt>
                    <dd className="font-medium">{details.changed_by_name}</dd>
                  </div>
                </dl>
                {details.observation && (
                  <div
                    className={cn(
                      "rounded-lg border-l-4 p-4",
                      approved
                        ? "border-green-600 bg-green-600/10"
                        : "border-red-600 bg-red-600/10"
                    )}
                  >
                    <p
                      className={cn(
                        "mb-1 text-xs font-bold uppercase tracking-wide",
                        approved
                          ? "text-green-700 dark:text-green-400"
                          : "text-red-700 dark:text-red-400"
                      )}
                    >
                      Observação
                    </p>
                    <p className="whitespace-pre-line text-base">
                      {details.observation}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <p className="whitespace-pre-line text-base">{current.message}</p>
            )}
          </div>
        </AlertDialogDescription>

        <AlertDialogFooter className="items-center border-t px-6 py-4 sm:justify-between">
          <span className="text-sm text-muted-foreground">
            {notifications.length > 1
              ? `Mais ${notifications.length - 1} aviso(s) na fila`
              : ""}
          </span>
          <AlertDialogAction
            onClick={handleAcknowledge}
            disabled={acknowledging}
            className={cn(
              "text-white",
              approved
                ? "bg-green-600 hover:bg-green-700"
                : "bg-red-600 hover:bg-red-700"
            )}
          >
            Ciente
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
